// Candado del OTP de la reanudación, del lado del front.
//
// El silo rebota 26 mutaciones con `RESUME_OTP_REQUIRED` (409) mientras una venta retomada espera su
// código nuevo — y el rebote no dice de qué sesión habla. Acá se le pone la sesión, que es el único
// dato que falta para poder mandar al vendedor al paso del código en vez de mostrarle un toast suelto.
import { ApiError } from '@/api/errors';

const RESUME_OTP_CODE = 'RESUME_OTP_REQUIRED';

/** Lo que lee el vendedor: qué pasó y qué tiene que hacer. */
export const RESUME_OTP_MESSAGE =
    'Retomaste esta venta: para seguir, confirma el código que le llegó al cliente.';

/** Rebote del candado ya con la venta a la que pertenece. */
export class ResumeOtpRequiredError extends ApiError {
    readonly sessionId: string;

    constructor(sessionId: string, source: ApiError) {
        super(source.httpStatus, {
            code: source.code,
            date: source.date,
            data: source.data,
            msg: RESUME_OTP_MESSAGE,
        });
        this.name = 'ResumeOtpRequiredError';
        this.sessionId = sessionId;
    }
}

// Endpoints que RESUELVEN el candado — el código y la propia reanudación. El silo no los protege, y
// etiquetarlos abriría el bucle que este guard existe para evitar: rebote → ir al código → rebote.
// `signature/send-otp` no entra: es el OTP de la firma del paso 08, otro código y otro momento.
const SETTLES_LOCK = /\/(otp(\/|$)|resume$)/;

const SESSION_IN_PATH = /^\/wizard\/sessions\/([^/?]+)/;

/**
 * Etiqueta el rebote del candado con su sesión; cualquier otro error sale intacto.
 *
 * Sin sesión en el path no se etiqueta: no habría a dónde mandar al vendedor.
 */
export function tagResumeOtpError(path: string, error: unknown): unknown {
    if (!(error instanceof ApiError) || error.code !== RESUME_OTP_CODE) return error;
    if (SETTLES_LOCK.test(path)) return error;
    const sessionId = SESSION_IN_PATH.exec(path)?.[1];
    return sessionId ? new ResumeOtpRequiredError(sessionId, error) : error;
}

/** Venta que hay que retomar, o `null` si el error es otro. Lo consume el manejo central de App.tsx. */
export function resumeOtpTarget(error: unknown): { sessionId: string } | null {
    return error instanceof ResumeOtpRequiredError ? { sessionId: error.sessionId } : null;
}
